import {Component} from 'react';
import h from 'react-hyperscript';
import BasicCard from 'Components/Cards/BasicCard';
import {h4, eyebrow} from 'Components/Typography/Typography';
import BarChartVertical from 'Components/DataViz/BarCharts/BarChartVertical';
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator';
import TooltipGroup from 'Components/TooltipGroup/TooltipGroup';
import {
  getAvg,
  setZeroIfInvalidNumber,
  isArrayEmpty} from 'Src/utils/utils';
import ReportError from 'Components/ErrorMessage/ReportError';
import {RESOURCE} from 'Src/utils/gtm/const';
import {pushEvent as pushGTMEvent} from 'Src/utils/gtm';

class ProductUsageDetails extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;

    this.largeViewportGraphSize = 1180;

    this.state = {
      productValues: null,
      isLoaded: false,
      graphContainerWidth: this.largeViewportGraphSize,
      activeStep: props.products[0].title.toLowerCase(),
    };

    this.setRef = this.setRef.bind(this);
    this.handleLabelClick = this.handleLabelClick.bind(this);
  }

  componentWillMount() {
    this._isMounted = true;
    this.setData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.screenWidth !== this.props.screenWidth && this.graphRef) {
      this.setGraphContainerWidth();
    }
  }

  setRef(ref) {
    this.graphRef = ref;

    this.setGraphContainerWidth();
  }

  setGraphContainerWidth() {
    const graphContainerWidth = this.graphRef.current.offsetWidth;

    if (
      this.state.graphContainerWidth !== graphContainerWidth &&
      this.props.dynamicGraphSize
    ) {
      this.setState({
        graphContainerWidth,
      });
    } else if (this.state.graphContainerWidth) {
      this.setState({
        graphContainerWidth: this.largeViewportGraphSize,
      });
    }
  }

  setData() {
    if (this.dataAvailable) {
      const {data, dataPrev} = this.props;

      const productValues = this.getProductValues(data);
      this.setProductMetrics('productValues', productValues);

      if (dataPrev) {
        const productValuesPrev = this.getProductValues(dataPrev);

        this.setProductMetrics('productValuesPrev', productValuesPrev);
      }
    } else {
      this.setHasError(true);
    }
  }

  get dataAvailable() {
    return (
      this.props.hasProductMetricsHistory &&
      !isArrayEmpty(this.props.data) &&
      !isArrayEmpty(this.props.metrics) &&
      !isArrayEmpty(this.props.products)
    );
  }

  setHasError(value) {
    this.setState({
      isLoaded: true,
      hasError: value,
    });
  }

  handleLabelClick(step) {
    this.setState({
      activeStep: step,
    });
  }

  getProductValues(data) {
    const productValues = data.map((product) => {
      const metrics = product.metrics.map((week) => {
        return week.metric;
      });
      return getAvg(metrics);
    });
    return productValues;
  }

  setProductMetrics(key, values) {
    if (this._isMounted) {
      this.setState(
          {
            [key]: values,
          },
          () => {
            this.setState({
              isLoaded: true,
            });
          }
      );
    }
  }

  createReviewBars(productValues) {
    if (!productValues) return;

    const reviewBars = this.props.products.map((product, i) => {
      let barValue = 0;
      if (productValues) {
        barValue = setZeroIfInvalidNumber(productValues[i]);
        barValue = barValue && barValue >= 1.0 ? 1.0 : barValue;
      }
      const percent = Math.floor(barValue * 100);
      const val = percent <= 100 ? percent : 100;

      return {
        name: product.title,
        value: barValue,
        icon: {
          standard: product.icon,
          retina: product.icon_2x,
        },
        valueFormatted: `${val}%`,
      };
    });

    return reviewBars;
  }

  render() {
    if (this.state.isLoaded) {
      const {activeStep, productValues, productValuesPrev} = this.state;
      const resourceAction = RESOURCE.ACTION.RESOURCE_CLICK;

      return h(
          BasicCard,
          {
            id: this.props.id,
            className: this.props.className,
          },
          [
            h(
                TooltipGroup,
                {
                  title: this.props.tooltip,
                },
                h(
                    h4,
                    {
                      key: 'title__key',
                      className: 'card__title',
                    },
                    this.props.title
                )
            ),
          this.state.hasError ?
            h(
                'div',
                {
                  className: 'padding-bottom--md',
                },
                h(
                    ReportError,
                    {
                      message: this.props.noDataError,
                    },
                    null
                )
            ) :
            h('div', null, [
              h(
                  eyebrow,
                  {
                    key: 'eyebrow__key',
                    className: 'padding-bottom--micro',
                  },
                  this.props.subtitle
              ),
              h(
                  'p',
                  {
                    key: 'help__text__key',
                    className:
                      'p text--grey text--small footnote padding-bottom--micro',
                  },
                  this.props.chartHelpText
              ),
              h(
                  BarChartVertical,
                  {
                    key: 'report__product-usage-details__graph__key',
                    id: 'report__product-usage-details__graph',
                    data: this.createReviewBars(productValues),
                    dataPrev: this.createReviewBars(productValuesPrev),
                    chartName: this.props.chartName,
                    chartNamePrev: this.props.chartNamePrev,
                    barWidth: 10,
                    shouldLog: true,
                    color: '#1a73e8',
                    parentId: this.props.id,
                    size: [this.state.graphContainerWidth, 157],
                    onLabelClick: this.handleLabelClick,
                    hasIcons: true,
                    hasIconsText: true,
                    setRef: this.setRef,
                    activeStep,
                  },
                  null
              ),
              h('div', {id: 'product-usage-details__resources'}, [
                h(
                    eyebrow,
                    {
                      className: 'padding-bottom--sm padding-top--sm',
                    },
                    `${this.props.resourcesHeadline} ${activeStep}`
                ),
                h(
                    'div',
                    {
                      className: `flex flex--wrap-sm padding-bottom--md
                        flex--space-between flex--align-start`,
                    },
                    this.props.resources[activeStep].map((item, i) => {
                      const href = item.href;

                      return h(
                          'a',
                          {
                            key: `content-item-${i}-content`,
                            className: `cta text--blue text--small-2
                              padding-right--xl col-ratio-1--sm`,
                            href,
                            target: '_blank',
                            onClick: () => {
                              pushGTMEvent(RESOURCE.TAG, {
                                event: RESOURCE.EVENT,
                                action: {
                                  resource_action: resourceAction,
                                },
                                variables: {
                                  resource_category:
                                    `Education Product Usage: ${activeStep}`,
                                  resource_url: href,
                                },
                              });
                            },
                          },
                          item.title
                      );
                    })
                ),
              ]),
            ]),
          ]
      );
    } else {
      return h(LoadingIndicator, null, null);
    }
  }
}

export default ProductUsageDetails;
